import {
	DesktopNav,
	MobileNav,
	SvgFromUrl,
	useDisplayType,
	type LayoutNavComponentProps,
} from '@ds-mgnl/marketing';
import { ButtonLink, classNames, type ButtonLinkProps } from '@dsx/react';
import type { ReactNode, RefObject } from 'react';
import {
	createRef,
	useCallback,
	useEffect,
	useMemo,
	useRef,
	useState,
} from 'react';
import styles from './TopNav.module.css';
import { TopNavBarMobile } from './TopNavBarMobile';
import { topNavData } from './topNavData';
import type { Logo, MenuContent, MenuLink } from './types';
import { updateActiveContent } from './utils';

const renderDesktopCTA = (ctaContent: MenuLink[], transparent?: boolean) => {
	if (ctaContent.length === 0) {
		return null;
	}

	let variants: Record<string, ButtonLinkProps['variant']> = {
		primary: transparent ? 'inverse-primary' : 'primary',
		default: transparent ? 'inverse-default' : 'default',
	};

	return (
		<div className={styles['top-nav-cta']}>
			{ctaContent.map(({ path, value, icon, label, openInNewTab }, index) => (
				<ButtonLink
					key={path}
					href={value}
					target={openInNewTab ? '_blank' : '_self'}
					rel="nofollow"
					variant={index == 0 ? variants['primary'] : variants['default']}
					className={classNames(styles['menu-item-link'])}
					icon={icon}
				>
					{label}
				</ButtonLink>
			))}
		</div>
	);
};

const initializeMenuRefs = (
	refs: RefObject<HTMLDivElement>[],
	menuContent: MenuContent[],
) =>
	Array(menuContent.length)
		.fill(null)
		.map((_, i) => refs[i] || createRef());

export interface TopNavProps extends LayoutNavComponentProps {
	activePath: string;
	initMenuContent: MenuContent[];
	ctaLinks: MenuLink[];
	logo: Logo;
	header?: ReactNode;
}

export function TopNav({
	activePath,
	initMenuContent,
	ctaLinks,
	logo,
	transparent,
}: TopNavProps) {
	const displayType = useDisplayType();
	const isMinDesktop = displayType === 'desktop';

	const [open, setOpen] = useState(false);

	// Derive menu content from props instead of managing in state
	const menuContent = useMemo(
		() => updateActiveContent(initMenuContent, activePath),
		[initMenuContent, activePath],
	);

	const menuRef = useRef<HTMLUListElement>(null);
	const toggleButtonRef = useRef<HTMLDivElement>(null);

	const [toggleMenuRefs] = useState<RefObject<HTMLDivElement>[]>(
		initializeMenuRefs([], menuContent),
	);

	const handleMobileMenuToggled = () => {
		setOpen((currentState) => !currentState);
	};

	// TODO: consider other scenarios for accesibility
	const handleClickOutside = useCallback(
		(event: MouseEvent) => {
			if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
				if (
					isMinDesktop &&
					toggleMenuRefs.every(
						(ref) => ref.current && !ref.current.contains(event.target as Node),
					)
				) {
					setOpen(false);
				} else if (
					toggleButtonRef.current &&
					!toggleButtonRef.current.contains(event.target as Node)
				) {
					setOpen(false);
				}
			}
		},
		[isMinDesktop, toggleMenuRefs],
	);

	useEffect(() => {
		document.addEventListener('mousedown', handleClickOutside);
		return () => {
			document.removeEventListener('mousedown', handleClickOutside);
		};
	}, [handleClickOutside]);

	return (
		<header
			className={classNames(styles['top-nav'], {
				[styles['transparent']]: transparent,
			})}
			key={activePath}
		>
			<div className={styles['top-nav-content']}>
				<div className={styles['top-nav-logo']}>
					<a href={'/'}>
						<SvgFromUrl
							url={logo.svg}
							className={styles['logo']}
							width={logo.width}
							height={logo.height}
						/>
					</a>
				</div>

				{isMinDesktop && (
					<div className={styles['top-nav-primary-content']}>
						<DesktopNav items={topNavData} transparent={transparent} />
						{renderDesktopCTA(ctaLinks, transparent)}
					</div>
				)}
				{!isMinDesktop && (
					<div>
						<TopNavBarMobile
							toggleButtonRef={toggleButtonRef}
							isOpen={open}
							onSelect={handleMobileMenuToggled}
							transparent={transparent}
						/>
						{open && (
							<ul ref={menuRef} className={styles['top-nav-mobile-menu']}>
								<MobileNav items={topNavData} />
								<div className={styles['ctas']}>
									{ctaLinks.map(
										({ path, value, icon, label, openInNewTab }, index) => (
											<ButtonLink
												key={path}
												href={value}
												target={openInNewTab ? '_blank' : '_self'}
												rel="nofollow"
												variant={index == 0 ? 'primary' : 'default'}
												className={classNames(styles['menu-item-link'])}
												icon={icon}
											>
												{label}
											</ButtonLink>
										),
									)}
								</div>
							</ul>
						)}
					</div>
				)}
			</div>
		</header>
	);
}
